import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Clock, Check, Send, Trash2, PenLine, UserX } from 'lucide-react';
import api from '../../utils/api';
import { getStatusLabel } from '../../utils/helpers';

export const MobileWorkOrdersPage = () => {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const loadWorkOrders = useCallback(async () => {
    try {
      const response = await api.getWorkOrders();
      // Backend already filters by user identity (created_by OR created_on_behalf_of)
      setWorkOrders(response.data);
    } catch (error) {
      console.error('Failed to load work orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadWorkOrders(); }, [loadWorkOrders]);

  const handleSubmit = async (id) => {
    try {
      await api.submitWorkOrder(id);
      alert('Bollettino inviato per revisione');
      loadWorkOrders();
    } catch (error) {
      alert(error.response?.data?.detail || 'Errore nell\'invio');
    }
  };

  const getStatusBadgeClass = (status) => {
    const map = {
      DRAFT: 'bg-slate-100 text-slate-600',
      IN_REVIEW: 'bg-amber-100 text-amber-700',
      NEEDS_CORRECTION: 'bg-red-100 text-red-700',
      APPROVED: 'bg-green-100 text-green-700',
      INVOICED: 'bg-blue-100 text-blue-700',
      CANCELLED: 'bg-slate-100 text-slate-400',
    };
    return map[status] || 'bg-slate-100 text-slate-600';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'DRAFT': return <Edit className="h-3.5 w-3.5" />;
      case 'IN_REVIEW': return <Clock className="h-3.5 w-3.5" />;
      case 'APPROVED': return <Check className="h-3.5 w-3.5" />;
      default: return null;
    }
  };

  const filteredOrders = workOrders.filter(wo => {
    if (filter === 'draft' && wo.status !== 'DRAFT') return false;
    if (filter === 'pending' && !['IN_REVIEW', 'NEEDS_CORRECTION'].includes(wo.status)) return false;
    if (filter === 'approved' && !['APPROVED', 'INVOICED'].includes(wo.status)) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (wo.number?.toLowerCase().includes(search) || wo.customer_name?.toLowerCase().includes(search) || wo.site_name?.toLowerCase().includes(search));
    }
    return true;
  });

  const filterButtons = [
    { key: 'all', label: 'Tutti', icon: null },
    { key: 'draft', label: 'Bozze', icon: Edit },
    { key: 'pending', label: 'In attesa', icon: Clock },
    { key: 'approved', label: 'Approvati', icon: Check },
  ];

  return (
    <div className="flex flex-col h-full" data-testid="mobile-work-orders-page">
      {/* Header with Search */}
      <div className="px-4 pt-4 pb-2 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Cerca bollettino..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="search-input"
          />
        </div>
        <button
          onClick={() => navigate('/m/bollettini/nuovo')}
          className="bg-blue-600 text-white p-2.5 rounded-xl"
          data-testid="add-btn"
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>

      {/* Filter Pills */}
      <div className="px-4 py-2 flex gap-2 overflow-x-auto no-scrollbar">
        {filterButtons.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              filter === key ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid={`filter-${key}`}
          >
            {Icon && <Icon className="h-3.5 w-3.5" />}
            {label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-4 py-2">
        {loading ? (
          <div className="text-center py-12 text-slate-400">Caricamento...</div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500">Nessun bollettino trovato</p>
            {filter !== 'all' && (
              <button onClick={() => setFilter('all')} className="text-blue-600 text-sm mt-2">Mostra tutti</button>
            )}
          </div>
        ) : (
          <div className="space-y-2 pb-4">
            {filteredOrders.map((wo) => (
              <div
                key={wo.id}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                data-testid={`wo-card-${wo.id}`}
              >
                <button
                  onClick={() => navigate(`/m/bollettini/${wo.id}`)}
                  className="w-full text-left p-4"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">{wo.number}</span>
                    <div className="flex items-center gap-1.5">
                      {wo.signature_base64 && (
                        <span
                          className="flex items-center text-green-600"
                          title={wo.signer_name ? `Firmato da ${wo.signer_name}` : 'Firmato'}
                          data-testid={`signed-icon-${wo.id}`}
                        >
                          <PenLine className="h-3.5 w-3.5" />
                        </span>
                      )}
                      {!wo.signature_base64 && wo.customer_not_present && (
                        <span
                          className="flex items-center text-amber-600"
                          title="Cliente non presente"
                          data-testid={`not-present-icon-${wo.id}`}
                        >
                          <UserX className="h-3.5 w-3.5" />
                        </span>
                      )}
                      <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(wo.status)}`}>
                        {getStatusIcon(wo.status)}
                        {getStatusLabel(wo.status)}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">{wo.customer_name}</p>
                  {wo.site_name && <p className="text-xs text-slate-400 mt-0.5">{wo.site_name}</p>}
                  <div className="text-xs text-slate-400 mt-1">{new Date(wo.created_at).toLocaleDateString('it-CH')}</div>
                </button>

                {/* Quick actions for drafts */}
                {(wo.status === 'DRAFT' || wo.status === 'NEEDS_CORRECTION') && (
                  <div className="flex border-t border-slate-100">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSubmit(wo.id); }}
                      className="flex-1 py-2 text-sm text-green-600 font-medium flex items-center justify-center gap-1 hover:bg-green-50"
                      data-testid={`submit-${wo.id}`}
                    >
                      <Send className="h-3.5 w-3.5" /> Invia
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileWorkOrdersPage;
